import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  confirmIndustrialCostDirectMaterialsImport,
  createIndustrialCostDirectCostCategory,
  createIndustrialCostDirectMaterial,
  deleteIndustrialCostDirectCostCategory,
  deleteIndustrialCostDirectMaterial,
  getIndustrialCostDirectCostCategories,
  getIndustrialCostDirectMaterials,
  previewIndustrialCostDirectMaterialsImport,
  updateIndustrialCostDirectCostCategory,
  updateIndustrialCostDirectMaterial,
} from '../services/industrialCostService';
import type {
  CreateDirectCostCategoryPayload,
  CreateDirectMaterialPayload,
  DirectCostCategory,
  DirectMaterial,
  DirectMaterialImportPreviewResponse,
  DirectMaterialType,
  UpdateDirectCostCategoryPayload,
  UpdateDirectMaterialPayload,
} from '../types/industrialCost';

type FormState = {
  code: string;
  name: string;
  material_type: DirectMaterialType | '';
  unit_label: string;
  is_active: boolean;
  description: string;
};

type CategoryFormState = {
  code: string;
  label: string;
  is_active: boolean;
  include_in_direct_unit_cost: boolean;
  include_in_material_mass: boolean;
  display_order: number;
  description: string;
};

const EMPTY_CATEGORY_FORM: CategoryFormState = {
  code: '',
  label: '',
  is_active: true,
  include_in_direct_unit_cost: true,
  include_in_material_mass: false,
  display_order: 0,
  description: '',
};

const EMPTY_FORM: FormState = {
  code: '',
  name: '',
  material_type: '',
  unit_label: '',
  is_active: true,
  description: '',
};

const directMaterialImportFields = [
  { key: 'code', label: 'Código', required: true },
  { key: 'name', label: 'Nombre', required: false },
  { key: 'material_type', label: 'Clasificación / tipo de material', required: false },
  { key: 'unit_label', label: 'Unidad', required: false },
  { key: 'description', label: 'Descripción', required: false },
  { key: 'is_active', label: 'Activo', required: false },
] as const;

function getCategoryLabel(
  categories: DirectCostCategory[],
  type: DirectMaterialType,
) {
  const category = categories.find((item) => item.code === type);
  return category?.label ?? type ?? '';
}

function buildPayload(form: FormState): CreateDirectMaterialPayload {
  const code = form.code.trim();

  return {
    code,
    name: form.name.trim() || code,
    material_type: form.material_type || null,
    unit_label: form.unit_label.trim() || null,
    is_active: form.is_active,
    description: form.description.trim() || null,
  };
}

export default function IndustrialCostDirectMaterialsView() {
  const [materials, setMaterials] = useState<DirectMaterial[]>([]);
  const [categories, setCategories] = useState<DirectCostCategory[]>([]);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [categoryForm, setCategoryForm] = useState<CategoryFormState>(EMPTY_CATEGORY_FORM);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [savingCategory, setSavingCategory] = useState<boolean>(false);
  const [categoryDialogError, setCategoryDialogError] = useState<string | null>(null);
  const [materialDialogOpen, setMaterialDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<'all' | 'unclassified' | DirectMaterialType>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importStep, setImportStep] = useState<1 | 2>(1);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<DirectMaterialImportPreviewResponse | null>(null);
  const [importMapping, setImportMapping] = useState<Record<string, string | null>>({});
  const [materialTypeMapping, setMaterialTypeMapping] = useState<Record<string, string | null>>({});
  const [previewingImport, setPreviewingImport] = useState(false);
  const [confirmingImport, setConfirmingImport] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [deletingMaterialId, setDeletingMaterialId] = useState<string | null>(null);
  const [deletingCategoryConfirm, setDeletingCategoryConfirm] = useState(false);

  async function loadData() {
    setLoading(true);
    setError(null);

    try {
      const [materialsData, categoriesData] = await Promise.all([
        getIndustrialCostDirectMaterials(),
        getIndustrialCostDirectCostCategories(),
      ]);

      setMaterials(materialsData);
      setCategories(categoriesData);
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          'No se pudo cargar el catálogo de materiales directos.',
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
  }, []);

  const filteredMaterials = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();

    return materials.filter((item) => {
      const categoryLabel = item.material_type
        ? getCategoryLabel(categories, item.material_type)
        : 'Sin clasificación';

      const matchesSearch =
        !normalizedSearch ||
        [
          item.code,
          item.name,
          categoryLabel,
          item.unit_label,
          item.description,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedSearch));

      const matchesType =
        typeFilter === 'all' ||
        (typeFilter === 'unclassified' && !item.material_type) ||
        item.material_type === typeFilter;

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && item.is_active) ||
        (statusFilter === 'inactive' && !item.is_active);

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [categories, materials, searchQuery, statusFilter, typeFilter]);

  function resetForm() {
    setEditingId(null);
    setForm({
      ...EMPTY_FORM,
      material_type: '',
    });
  }

  function resetCategoryForm() {
    setEditingCategoryId(null);
    setCategoryForm(EMPTY_CATEGORY_FORM);
    setCategoryDialogError(null);
  }

  function openCreateMaterialDialog() {
    resetForm();
    setMaterialDialogOpen(true);
  }

  function openCreateCategoryDialog() {
    resetCategoryForm();
    setCategoryDialogOpen(true);
  }

  function startEditCategory(category: DirectCostCategory) {
    setEditingCategoryId(category.id);
    setCategoryForm({
      code: category.code,
      label: category.label,
      is_active: category.is_active,
      include_in_direct_unit_cost: category.include_in_direct_unit_cost,
      include_in_material_mass: category.include_in_material_mass,
      display_order: category.display_order,
      description: category.description ?? '',
    });
    setError(null);
    setCategoryDialogError(null);
    setSuccessMessage(null);
    setCategoryDialogOpen(true);
  }

  async function handleSubmitCategory() {
    setSavingCategory(true);
    setError(null);
    setCategoryDialogError(null);
    setSuccessMessage(null);

    try {
      const payload: CreateDirectCostCategoryPayload = {
        code: categoryForm.code.trim(),
        label: categoryForm.label.trim(),
        is_active: categoryForm.is_active,
        include_in_direct_unit_cost: categoryForm.include_in_direct_unit_cost,
        include_in_material_mass: categoryForm.include_in_material_mass,
        display_order: Number(categoryForm.display_order) || 0,
        description: categoryForm.description.trim() || null,
      };

      if (!payload.code) {
        throw new Error('Debes informar el código de la clasificación.');
      }

      if (!payload.label) {
        throw new Error('Debes informar el nombre de la clasificación.');
      }

      if (editingCategoryId) {
        const updatePayload: UpdateDirectCostCategoryPayload = {
          label: payload.label,
          is_active: payload.is_active,
          include_in_direct_unit_cost: payload.include_in_direct_unit_cost,
          include_in_material_mass: payload.include_in_material_mass,
          display_order: payload.display_order,
          description: payload.description,
        };
        await updateIndustrialCostDirectCostCategory(editingCategoryId, updatePayload);
        setSuccessMessage('Clasificación actualizada correctamente.');
      } else {
        await createIndustrialCostDirectCostCategory(payload);
        setSuccessMessage('Clasificación creada correctamente.');
      }

      resetCategoryForm();
      setCategoryDialogOpen(false);
      await loadData();
    } catch (err: any) {
      setCategoryDialogError(
        err?.response?.data?.detail ||
          err?.message ||
          'No se pudo guardar la clasificación.',
      );
    } finally {
      setSavingCategory(false);
    }
  }

  async function handleDeleteCategory() {
    if (!editingCategoryId) return;
    setDeletingCategoryConfirm(true);
  }

  async function handleConfirmDeleteCategory() {
    if (!editingCategoryId) return;
    setDeletingCategoryConfirm(false);
    setSavingCategory(true);
    setError(null);
    setCategoryDialogError(null);
    setSuccessMessage(null);

    try {
      await deleteIndustrialCostDirectCostCategory(editingCategoryId);
      setSuccessMessage('Clasificación eliminada correctamente.');
      resetCategoryForm();
      setCategoryDialogOpen(false);
      await loadData();
    } catch (err: any) {
      setCategoryDialogError(
        err?.response?.data?.detail ||
          err?.message ||
          'No se pudo eliminar la clasificación.',
      );
    } finally {
      setSavingCategory(false);
    }
  }

  function startEdit(item: DirectMaterial) {
    setEditingId(item.id);
    setForm({
      code: item.code,
      name: item.name,
      material_type: item.material_type ?? '',
      unit_label: item.unit_label ?? '',
      is_active: item.is_active,
      description: item.description ?? '',
    });
    setError(null);
    setSuccessMessage(null);
    setMaterialDialogOpen(true);
  }

  async function handleSubmit() {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const payload = buildPayload(form);

      if (!payload.code) {
        throw new Error('Debes informar el código del material.');
      }

      if (editingId) {
        await updateIndustrialCostDirectMaterial(
          editingId,
          payload as UpdateDirectMaterialPayload,
        );
        setSuccessMessage('Material actualizado correctamente.');
      } else {
        await createIndustrialCostDirectMaterial(payload);
        setSuccessMessage('Material creado correctamente.');
      }

      resetForm();
      setMaterialDialogOpen(false);
      await loadData();
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          'No se pudo guardar el material directo.',
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(materialId: string) {
    setDeletingMaterialId(materialId);
  }

  async function handleConfirmDeleteMaterial() {
    if (!deletingMaterialId) return;
    const materialId = deletingMaterialId;
    setDeletingMaterialId(null);
    setError(null);
    setSuccessMessage(null);

    try {
      await deleteIndustrialCostDirectMaterial(materialId);
      if (editingId === materialId) {
        resetForm();
      }
      setSuccessMessage('Material eliminado correctamente.');
      await loadData();
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          'No se pudo eliminar el material directo.',
      );
    }
  }

  function buildInitialMaterialTypeMapping(
    preview: DirectMaterialImportPreviewResponse,
  ): Record<string, string | null> {
    const nextMapping: Record<string, string | null> = {};

    preview.detected_material_type_values.forEach((item) => {
      nextMapping[item.source_value] = item.resolved_material_type ?? null;
    });

    return nextMapping;
  }

  async function runImportPreview(
    file: File,
    mapping?: Record<string, string | null>,
  ) {
    setPreviewingImport(true);
    setImportError(null);
    setError(null);
    setSuccessMessage(null);

    try {
      const preview = await previewIndustrialCostDirectMaterialsImport(file, mapping);

      setImportPreview(preview);
      setImportMapping(preview.auto_mapping);
      setMaterialTypeMapping(buildInitialMaterialTypeMapping(preview));
      setImportDialogOpen(true);
      setImportStep(1);
    } catch (err: any) {
      setImportError(
        err?.response?.data?.detail ||
          err?.message ||
          'No se pudo leer el archivo de materiales.',
      );
    } finally {
      setPreviewingImport(false);
    }
  }

  function handleSelectImportFile(file: File | null) {
    if (!file) return;

    setImportFile(file);
    setImportPreview(null);
    setImportMapping({});
    setMaterialTypeMapping({});
    setImportDialogOpen(true);
    setImportStep(1);
    void runImportPreview(file);
  }

  function handleCloseImportDialog() {
    if (confirmingImport) return;

    setImportDialogOpen(false);
    setImportStep(1);
    setImportFile(null);
    setImportPreview(null);
    setImportMapping({});
    setMaterialTypeMapping({});
    setImportError(null);

    if (importInputRef.current) {
      importInputRef.current.value = '';
    }
  }

  function handleOpenImportPicker() {
    importInputRef.current?.click();
  }

  function handleChangeImportMapping(field: string, column: string) {
    if (!importFile) return;

    const nextMapping = {
      ...importMapping,
      [field]: column || null,
    };

    setImportMapping(nextMapping);
    void runImportPreview(importFile, nextMapping);
  }

  function handleChangeMaterialTypeMapping(sourceValue: string, categoryCode: string) {
    setMaterialTypeMapping((current) => ({
      ...current,
      [sourceValue]: categoryCode || null,
    }));
  }

  async function handleConfirmImport() {
    if (!importFile || !importPreview) return;

    setConfirmingImport(true);
    setImportError(null);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await confirmIndustrialCostDirectMaterialsImport(
        importFile,
        importMapping,
        materialTypeMapping,
      );

      setSuccessMessage(
        `Importación completada: ${result.rows_created} creados, ${result.rows_updated} actualizados.`,
      );

      handleCloseImportDialog();
      await loadData();
    } catch (err: any) {
      setImportError(
        err?.response?.data?.detail ||
          err?.message ||
          'No se pudo confirmar la importación de materiales.',
      );
    } finally {
      setConfirmingImport(false);
    }
  }

  const canContinueImport =
    Boolean(importPreview) &&
    (importPreview?.missing_required_fields.length ?? 1) === 0;

  const importMaterialTypeColumnMapped =
    Boolean(importPreview?.coverage_summary.material_type_column_mapped);

  const activeMaterialsCount = materials.filter((material) => material.is_active).length;
  const unclassifiedMaterialsCount = materials.filter((material) => !material.material_type).length;
  const activeCategoriesCount = categories.filter((category) => category.is_active).length;

  const categoryUsageRows = categories.map((category) => ({
    category,
    usageCount: materials.filter((material) => material.material_type === category.code).length,
  }));

  const editingCategoryUsageCount = editingCategoryId
    ? materials.filter((material) => material.material_type === categoryForm.code).length
    : 0;

  return (
    <Stack spacing={3}>
      <input
        ref={importInputRef}
        type="file"
        accept=".csv,.xlsx"
        hidden
        onChange={(event) => handleSelectImportFile(event.target.files?.[0] ?? null)}
      />
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.66)',
          backdropFilter: 'blur(10px)',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2.5, md: 3 },
        }}
      >
        <Stack spacing={1.25}>
          <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 900 }}>
            Materiales directos
          </Typography>

          <Typography variant="body1" sx={{ color: '#cbd5e1', lineHeight: 1.7 }}>
            Catálogo maestro de materiales directos del sistema. Esta vista define
            qué inputs existen realmente en el modelo y deja disponibles esos
            materiales para Entradas y Consumos técnicos.
          </Typography>

          <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.65 }}>
            Aquí no se registran compras ni precios. Aquí se mantiene el universo
            controlado de materiales que luego utilizarán las capas operativas.
          </Typography>
        </Stack>
      </Paper>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.66)',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2.5, md: 3 },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 3fr) minmax(260px, 1fr)' },
            gap: 2.5,
            alignItems: 'stretch',
          }}
        >
          <Stack spacing={2.25}>
            <Box>
              <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                Clasificaciones del catálogo
              </Typography>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem', mt: 0.5 }}>
                Agrupan materiales y determinan cómo se trasladarán después a costes directos.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 1.25,
              }}
            >
              {[
                { label: 'Materiales totales', value: materials.length },
                { label: 'Activos', value: activeMaterialsCount },
                { label: 'Sin clasificación', value: unclassifiedMaterialsCount },
                { label: 'Clasificaciones activas', value: activeCategoriesCount },
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(2,6,23,0.32)',
                    px: 1.75,
                    py: 1.35,
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '1.35rem' }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {categoryUsageRows.map(({ category, usageCount }) => (
                <Chip
                  key={category.id}
                  label={`${category.label} · ${usageCount}${category.include_in_material_mass ? ' · KG MP' : ''}`}
                  variant={category.is_active ? 'filled' : 'outlined'}
                  onClick={() => startEditCategory(category)}
                  title={`${category.include_in_direct_unit_cost ? 'Incluida' : 'No incluida'} en coste unitario · ${category.include_in_material_mass ? 'Incluida' : 'No incluida'} en kg de materia prima · Haz clic para editar`}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: category.include_in_direct_unit_cost
                      ? 'rgba(56,189,248,0.12)'
                      : 'rgba(148,163,184,0.08)',
                    color: category.include_in_direct_unit_cost ? '#38bdf8' : '#94a3b8',
                    border: `1px solid ${category.include_in_direct_unit_cost ? 'rgba(56,189,248,0.30)' : 'rgba(148,163,184,0.20)'}`,
                    '&:hover': { opacity: 0.82, outline: `1px solid ${category.include_in_direct_unit_cost ? 'rgba(56,189,248,0.5)' : 'rgba(148,163,184,0.4)'}` },
                  }}
                />
              ))}

              <Chip
                label={`Sin clasificación · ${unclassifiedMaterialsCount}`}
                variant="outlined"
              />
            </Box>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(34,211,238,0.18)',
              bgcolor: 'rgba(8,47,73,0.16)',
              p: 2,
              alignSelf: 'stretch',
            }}
          >
            <Stack spacing={1.5} sx={{ height: '100%' }}>
              <Box>
                <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                  Acciones
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem', mt: 0.35 }}>
                  Alta manual o carga masiva del maestro.
                </Typography>
              </Box>

              <Stack spacing={1} sx={{ mt: 'auto' }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleOpenImportPicker}
                  disabled={previewingImport}
                >
                  {previewingImport ? 'Leyendo archivo...' : 'Importar materiales'}
                </Button>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={openCreateCategoryDialog}
                >
                  Crear clasificación
                </Button>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={openCreateMaterialDialog}
                >
                  Crear material
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.66)',
          backdropFilter: 'blur(10px)',
          overflow: 'hidden',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ px: 3, py: 2.5 }}
        >
          <Stack spacing={0.5}>
            <Typography sx={{ color: '#f8fafc', fontWeight: 800, fontSize: '1rem' }}>
              Catálogo actual
            </Typography>
            <Typography sx={{ color: '#94a3b8' }}>
              Materiales disponibles para Entradas, Consumos técnicos y futuras
              derivaciones de coste.
            </Typography>
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.25}
            sx={{ minWidth: { xs: '100%', md: 560 } }}
          >
            <TextField
              size="small"
              label="Buscar"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Código, nombre, clasificación..."
              sx={{ minWidth: { xs: '100%', md: 260 }, flex: 1 }}
            />

            <TextField
              select
              size="small"
              label="Clasificación"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as 'all' | 'unclassified' | DirectMaterialType)}
              sx={{ minWidth: { xs: '100%', md: 190 } }}
            >
              <MenuItem value="all">Todas las clasificaciones</MenuItem>
              <MenuItem value="unclassified">Sin clasificación</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.code}>
                  {category.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Estado"
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')
              }
              sx={{ minWidth: { xs: '100%', md: 150 } }}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="active">Activos</MenuItem>
              <MenuItem value="inactive">Inactivos</MenuItem>
            </TextField>
          </Stack>
        </Stack>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Unidad</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredMaterials.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.code}</TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 700 }}>{item.name}</Typography>
                  </TableCell>
                  <TableCell>{item.material_type ? getCategoryLabel(categories, item.material_type) : 'Sin clasificación'}</TableCell>
                  <TableCell>{item.unit_label || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={item.is_active ? 'Activo' : 'Inactivo'}
                      color={item.is_active ? 'success' : 'default'}
                      variant={item.is_active ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>{item.description || '—'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" onClick={() => startEdit(item)}>
                        Editar
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => void handleDelete(item.id)}
                      >
                        Eliminar
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}

              {!loading && filteredMaterials.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Alert severity="info" sx={{ my: 1 }}>
                      Todavía no hay materiales directos registrados para este filtro.
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </Box>
      </Paper>


      <Dialog
        open={categoryDialogOpen}
        onClose={() => {
          if (!savingCategory) {
            setCategoryDialogOpen(false);
            setCategoryDialogError(null);
          }
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingCategoryId ? 'Editar clasificación' : 'Crear clasificación'}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {categoryDialogError ? (
              <Alert severity="error">{categoryDialogError}</Alert>
            ) : null}

            {editingCategoryId ? (
              <Alert severity={editingCategoryUsageCount > 0 ? 'warning' : 'info'}>
                Esta clasificación está asignada a {editingCategoryUsageCount} materiales.
                {editingCategoryUsageCount > 0
                  ? ' No podrá eliminarse hasta reasignar esos materiales. Puedes desactivarla si no quieres seguir usándola en nuevas altas o importaciones.'
                  : ' Al no estar usada, puede eliminarse si ya no es necesaria.'}
              </Alert>
            ) : (
              <Alert severity="info">
                El código será el identificador estable de la clasificación. Después no se editará desde esta pantalla.
              </Alert>
            )}

            <TextField
              label="Código *"
              value={categoryForm.code}
              disabled={Boolean(editingCategoryId)}
              onChange={(e) => setCategoryForm((prev) => ({ ...prev, code: e.target.value }))}
              helperText={
                editingCategoryId
                  ? 'Código técnico estable. No se modifica para no romper materiales ya clasificados.'
                  : 'Código corto y estable. Ejemplo: MP, ENV, SUBP.'
              }
              fullWidth
            />

            <TextField
              label="Nombre"
              value={categoryForm.label}
              onChange={(e) => setCategoryForm((prev) => ({ ...prev, label: e.target.value }))}
              fullWidth
            />

            <TextField
              label="Orden"
              type="number"
              value={categoryForm.display_order}
              onChange={(e) =>
                setCategoryForm((prev) => ({
                  ...prev,
                  display_order: Number(e.target.value) || 0,
                }))
              }
              fullWidth
            />

            <TextField
              select
              label="Estado"
              value={categoryForm.is_active ? 'active' : 'inactive'}
              onChange={(e) =>
                setCategoryForm((prev) => ({
                  ...prev,
                  is_active: e.target.value === 'active',
                }))
              }
              fullWidth
            >
              <MenuItem value="active">Activa</MenuItem>
              <MenuItem value="inactive">Inactiva</MenuItem>
            </TextField>

            <TextField
              select
              label="Uso en coste directo"
              value={categoryForm.include_in_direct_unit_cost ? 'yes' : 'no'}
              onChange={(e) =>
                setCategoryForm((prev) => ({
                  ...prev,
                  include_in_direct_unit_cost: e.target.value === 'yes',
                }))
              }
              fullWidth
            >
              <MenuItem value="yes">Incluye en coste directo unitario</MenuItem>
              <MenuItem value="no">Solo clasificación informativa</MenuItem>
            </TextField>

            <TextField
              select
              label="Uso en kg de materia prima"
              value={categoryForm.include_in_material_mass ? 'yes' : 'no'}
              onChange={(e) =>
                setCategoryForm((prev) => ({
                  ...prev,
                  include_in_material_mass: e.target.value === 'yes',
                }))
              }
              helperText="Marca Sí para materias primas o compuestos que deben sumar en los kg del Panel de planta. Cajas, embalajes y otros componentes deben quedar en No."
              fullWidth
            >
              <MenuItem value="yes">Incluye en kg de materia prima</MenuItem>
              <MenuItem value="no">No incluye en kg de materia prima</MenuItem>
            </TextField>

            <TextField
              label="Descripción"
              value={categoryForm.description}
              onChange={(e) => setCategoryForm((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Box>
            {editingCategoryId ? (
              <Button
                color="error"
                onClick={() => void handleDeleteCategory()}
                disabled={savingCategory}
              >
                Eliminar clasificación
              </Button>
            ) : null}
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              onClick={() => {
                setCategoryDialogOpen(false);
                setCategoryDialogError(null);
              }}
              disabled={savingCategory}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              onClick={() => void handleSubmitCategory()}
              disabled={savingCategory}
            >
              {editingCategoryId ? 'Guardar cambios' : 'Crear clasificación'}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog open={materialDialogOpen} onClose={() => setMaterialDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          {editingId ? 'Editar material' : 'Crear material'}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Código *"
              value={form.code}
              disabled={Boolean(editingId)}
              onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
              fullWidth
            />

            <TextField
              label="Nombre"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
            />

            <TextField
              select
              label="Clasificación"
              value={form.material_type}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  material_type: e.target.value as DirectMaterialType | '',
                }))
              }
              fullWidth
            >
              <MenuItem value="">
                Sin clasificación
              </MenuItem>

              {categories
                .filter((category) => category.is_active)
                .map((category) => (
                  <MenuItem key={category.id} value={category.code}>
                    {category.label}
                  </MenuItem>
                ))}
            </TextField>

            <TextField
              label="Unidad"
              value={form.unit_label}
              onChange={(e) => setForm((prev) => ({ ...prev, unit_label: e.target.value }))}
              fullWidth
            />

            <TextField
              select
              label="Estado"
              value={form.is_active ? 'active' : 'inactive'}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  is_active: e.target.value === 'active',
                }))
              }
              fullWidth
            >
              <MenuItem value="active">Activo</MenuItem>
              <MenuItem value="inactive">Inactivo</MenuItem>
            </TextField>

            <TextField
              label="Descripción"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setMaterialDialogOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void handleSubmit()} disabled={saving}>
            {editingId ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={importDialogOpen}
        onClose={handleCloseImportDialog}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          Importar materiales directos
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {importError ? (
              <Alert severity="error">{importError}</Alert>
            ) : null}

            {importPreview?.warnings?.map((warning, index) => (
              <Alert severity="warning" key={`${warning.type ?? 'warning'}-${index}`}>
                {String(
                  warning.message ||
                    'Hay advertencias en la previsualización de la importación.',
                )}
              </Alert>
            ))}

            {importPreview ? (() => {
              const preview = importPreview;

              return (
              <>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr 1fr',
                      md: 'repeat(4, minmax(0, 1fr))',
                    },
                  }}
                >
                  {[
                    {
                      label: 'Filas',
                      value: preview.coverage_summary.total_rows,
                    },
                    {
                      label: 'Códigos únicos',
                      value: preview.coverage_summary.unique_codes_count,
                    },
                    {
                      label: 'Nuevos',
                      value: preview.coverage_summary.new_codes_count,
                    },
                    {
                      label: 'Existentes',
                      value: preview.coverage_summary.existing_codes_count,
                    },
                    {
                      label: 'Duplicados ignorados',
                      value: preview.coverage_summary.duplicate_rows_count,
                    },
                    {
                      label: 'Sin nombre',
                      value: preview.coverage_summary.rows_without_name,
                    },
                    {
                      label: 'Valores tipo detectados',
                      value: preview.coverage_summary.detected_material_type_values_count,
                    },
                    {
                      label: 'Tipos sin resolver',
                      value: preview.coverage_summary.unresolved_material_type_values_count,
                    },
                  ].map((item) => (
                    <Paper
                      key={item.label}
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: '1px solid rgba(148,163,184,0.16)',
                        bgcolor: 'rgba(15,23,42,0.58)',
                        px: 2,
                        py: 1.5,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '1.25rem' }}>
                        {item.value}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>

                <Stack direction="row" spacing={1}>
                  <Chip
                    label="1 · Columnas"
                    color={importStep === 1 ? 'primary' : 'default'}
                    onClick={() => setImportStep(1)}
                  />
                  <Chip
                    label="2 · Clasificaciones"
                    color={importStep === 2 ? 'primary' : 'default'}
                    onClick={() => {
                      if (preview.missing_required_fields.length === 0) setImportStep(2);
                    }}
                    variant={preview.missing_required_fields.length === 0 ? 'filled' : 'outlined'}
                  />
                </Stack>

                {importStep === 1 ? (
                  <Stack spacing={2}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                      Mapeo de columnas
                    </Typography>

                    {preview.missing_required_fields.length > 0 ? (
                      <Alert severity="error">
                        Falta mapear campos obligatorios:{' '}
                        {preview.missing_required_fields.join(', ')}
                      </Alert>
                    ) : null}

                    {!importMaterialTypeColumnMapped ? (
                      <Alert severity="warning">
                        No se ha mapeado una columna de clasificación. Puedes continuar,
                        pero todos los materiales importados quedarán como Sin clasificación.
                      </Alert>
                    ) : null}

                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Campo PROA</TableCell>
                          <TableCell>Columna del archivo</TableCell>
                          <TableCell>Obligatorio</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {directMaterialImportFields.map((field) => (
                          <TableRow key={field.key}>
                            <TableCell>
                              <Typography sx={{ fontWeight: 700 }}>
                                {field.label}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <TextField
                                select
                                size="small"
                                fullWidth
                                value={importMapping[field.key] ?? ''}
                                onChange={(event) =>
                                  handleChangeImportMapping(field.key, event.target.value)
                                }
                              >
                                <MenuItem value="">No mapear</MenuItem>
                                {preview.columns.map((column) => (
                                  <MenuItem key={column} value={column}>
                                    {column}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </TableCell>

                            <TableCell>
                              {field.required ? (
                                <Chip size="small" color="warning" label="Sí" />
                              ) : (
                                <Chip size="small" variant="outlined" label="No" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    <Box>
                      <Typography sx={{ color: '#f8fafc', fontWeight: 800, mb: 1 }}>
                        Vista previa
                      </Typography>

                      <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Código</TableCell>
                              <TableCell>Nombre</TableCell>
                              <TableCell>Clasificación origen</TableCell>
                              <TableCell>Unidad</TableCell>
                              <TableCell>Activo</TableCell>
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {preview.preview_rows.map((row, index) => (
                              <TableRow key={index}>
                                <TableCell>{String(row.code ?? '—')}</TableCell>
                                <TableCell>{String(row.name ?? '—')}</TableCell>
                                <TableCell>{String(row.material_type ?? '—')}</TableCell>
                                <TableCell>{String(row.unit_label ?? '—')}</TableCell>
                                <TableCell>{String(row.is_active ?? '—')}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Box>
                  </Stack>
                ) : (
                  <Stack spacing={2}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                      Mapeo de valores de clasificación
                    </Typography>

                    {!importMaterialTypeColumnMapped ? (
                      <Alert severity="warning">
                        No hay columna de clasificación mapeada. Si confirmas, todos
                        los materiales se importarán como Sin clasificación.
                      </Alert>
                    ) : null}

                    {importMaterialTypeColumnMapped &&
                    preview.detected_material_type_values.length === 0 ? (
                      <Alert severity="info">
                        La columna de clasificación no contiene valores informados.
                        Los materiales sin valor quedarán como Sin clasificación.
                      </Alert>
                    ) : null}

                    {importMaterialTypeColumnMapped &&
                    preview.detected_material_type_values.length > 0 ? (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Valor en Excel</TableCell>
                            <TableCell>Filas</TableCell>
                            <TableCell>Clasificación PROA</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {preview.detected_material_type_values.map((item) => (
                            <TableRow key={item.normalized_value}>
                              <TableCell>
                                <Typography sx={{ fontWeight: 700 }}>
                                  {item.source_value}
                                </Typography>
                                {item.is_resolved ? (
                                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                    Coincidencia automática por{' '}
                                    {item.resolution_source === 'category_code'
                                      ? 'código'
                                      : 'nombre'}
                                  </Typography>
                                ) : null}
                              </TableCell>

                              <TableCell>{item.row_count}</TableCell>

                              <TableCell>
                                <TextField
                                  select
                                  size="small"
                                  fullWidth
                                  value={materialTypeMapping[item.source_value] ?? ''}
                                  onChange={(event) =>
                                    handleChangeMaterialTypeMapping(
                                      item.source_value,
                                      event.target.value,
                                    )
                                  }
                                >
                                  <MenuItem value="">Sin clasificación</MenuItem>
                                  {categories
                                    .filter((category) => category.is_active)
                                    .map((category) => (
                                      <MenuItem key={category.id} value={category.code}>
                                        {category.label}
                                      </MenuItem>
                                    ))}
                                </TextField>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : null}
                  </Stack>
                )}
              </>
              );
            })() : (
              <Alert severity="info">
                Selecciona un archivo CSV o XLSX para preparar la importación.
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseImportDialog} disabled={confirmingImport}>
            Cancelar
          </Button>

          {importStep === 1 ? (
            <Button
              variant="contained"
              onClick={() => setImportStep(2)}
              disabled={!canContinueImport || previewingImport}
            >
              Continuar
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => void handleConfirmImport()}
              disabled={!canContinueImport || confirmingImport}
            >
              {confirmingImport ? 'Importando...' : 'Confirmar importación'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deletingMaterialId}
        onClose={() => setDeletingMaterialId(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Eliminar material directo</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            Vas a eliminar este material directo. Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setDeletingMaterialId(null)}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleConfirmDeleteMaterial()}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deletingCategoryConfirm}
        onClose={() => setDeletingCategoryConfirm(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Eliminar clasificación</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            Solo se eliminará si no está usada por ningún material. Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setDeletingCategoryConfirm(false)}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleConfirmDeleteCategory()}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}